import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { normaliseLookupInput } from '../../utils/lookupInput';
import TransitionNotice, { SupportPhone } from '../common/TransitionNotice';
import { guarantorLookupHint } from '../../utils/guarantorLookupHint';

interface GNVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  gnInput: string;
  setGnInput: (value: string) => void;
  lastNameInput: string;
  setLastNameInput: (value: string) => void;
  loading: boolean;
}

const GNVerificationModal: React.FC<GNVerificationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  gnInput,
  setGnInput,
  lastNameInput,
  setLastNameInput,
  loading
}) => {
  const { theme } = useTheme();
  
  if (!isOpen) return null;

  // Uses the SAME normaliser the submit path uses. Previously this checked .trim() while the submit
  // sent the raw value, so the two could disagree: a value of only a non-breaking space enabled the
  // button and then submitted nothing.
  const submitDisabled =
    !normaliseLookupInput(gnInput) || !normaliseLookupInput(lastNameInput) || loading;

  // Advisory hint only. NOT part of submitDisabled -- see the note at its render site. Gated per
  // tenant: only St. Luke's patients can be holding a Cerner encounter FIN, so only they should be
  // told about one. showGarantorNumberField is true for `default` and `axis` as well.
  const gnHint = theme?.showEncounterFinHint ? guarantorLookupHint(gnInput) : null;
  
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 modal-backdrop p-4">
      {/* Height bound and scroll come from `.modal-content` in LinkPayment.css. Do not add max-h-* or
          overflow-* here: that rule is emitted after Tailwind's utilities and wins, which is how a
          `max-h-[90vh] overflow-y-auto` here once rendered as capped and unscrollable. */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg w-[95%] max-w-md mx-auto modal-content">
        <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">Verify Your Information</h2>
        <p className="mb-4 sm:mb-6 text-gray-600 text-sm sm:text-base">{theme?.guarantorFieldIntro ?? 'Please enter your guarantor number and last name to access your payment details.'}</p>

        <div className="mb-3 sm:mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">{theme?.guarantorFieldLabel ?? 'Guarantor Number'}</label>
          <input
            type="text"
            value={gnInput}
            onChange={(e) => setGnInput(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
            placeholder={theme?.guarantorFieldPlaceholder ?? 'Enter your guarantor number'}
            disabled={loading}
            autoFocus
          />
          {/* Advisory only, and deliberately never disables Verify: a Cerner statement shows the
              per-encounter FIN far more prominently than the guarantor CMRN, so typing a FIN is a
              likely first attempt, and the lookup answers it with the same "No accounts found" as a
              wrong surname. Length-based, not prefix-based -- real CMRNs start 89 and 90. */}
          {gnHint && (
            <p className="mt-2 text-xs leading-relaxed text-amber-700" role="status" aria-live="polite">
              {gnHint}
            </p>
          )}
        </div>
        
        <div className="mb-4 sm:mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Last Name</label>
          <input
            type="text"
            value={lastNameInput}
            onChange={(e) => setLastNameInput(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
            placeholder="Enter your last name"
            disabled={loading}
          />
        </div>
        
        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
          <button 
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-smooth text-sm sm:text-base"
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            onClick={onSubmit}
            disabled={submitDisabled}
            className={`w-full sm:w-auto px-4 py-2 rounded-md text-white button-hover text-sm sm:text-base ${theme["button-bg"]} ${
              submitDisabled ? 'opacity-60 cursor-not-allowed' : ''
            }`}
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </div>

        {/* The support number, directly under the buttons so it stays on screen with them. This screen
            has no ContactBanner, so it is the only number here, and a patient whose verify fails comes
            straight back to this modal. Renders nothing for a tenant without a supportPhone. */}
        <SupportPhone phone={theme?.supportPhone} className="mt-3 text-center text-sm text-slate-600" />

        {/* Go-live notice. THIS is the screen St. Luke patients actually arrive on (the payment-link
            journey), so it has to be here and not only on the username/password login. Self-gating:
            a tenant whose theme carries no notice copy renders nothing. Its phone is suppressed because
            SupportPhone above already carries it.

            BELOW the form, never above: above, its height pushed Last Name and Verify off common laptop
            and phone screens. `empty:hidden` keeps tenants with no notice free of a stray margin. */}
        <div className="mt-4 sm:mt-6 empty:hidden">
          <TransitionNotice theme={theme} showPhone={false} />
        </div>
      </div>
    </div>
  );
};

export default GNVerificationModal; 